import states from 'states-us';

/**
 * Australian states and territories mapping
 * Maps various formats (abbreviations, common names) to full state names
 */
const AU_STATES = [
  { abbreviation: 'ACT', names: ['ACT', 'Australian Capital Territory'], fullName: 'Australian Capital Territory' },
  { abbreviation: 'NSW', names: ['NSW', 'New South Wales'], fullName: 'New South Wales' },
  { abbreviation: 'NT', names: ['NT', 'Northern Territory'], fullName: 'Northern Territory' },
  { abbreviation: 'QLD', names: ['QLD', 'Queensland'], fullName: 'Queensland' },
  { abbreviation: 'SA', names: ['SA', 'South Australia'], fullName: 'South Australia' },
  { abbreviation: 'TAS', names: ['TAS', 'Tasmania'], fullName: 'Tasmania' },
  { abbreviation: 'VIC', names: ['VIC', 'Vic', 'Victoria'], fullName: 'Victoria' },
  { abbreviation: 'WA', names: ['WA', 'Western Australia'], fullName: 'Western Australia' },
];

/**
 * Get US states mapping from states-us package
 * @returns {Array} Array of state objects with name and abbreviation
 */
export const getUSStates = () => {
  return states && states.length > 0 ? states : [];
};

/**
 * Get Australian states mapping
 * @returns {Array} Array of state objects with abbreviation, names array, and fullName
 */
export const getAUStates = () => {
  return AU_STATES;
};

/**
 * Get Australian states formatted for dropdown select (compatible with US states format)
 * @returns {Array} Array of state objects with name and abbreviation properties
 */
export const getAUStatesForSelect = () => {
  return AU_STATES.map(state => ({
    name: state.fullName,
    abbreviation: state.abbreviation,
  }));
};

/**
 * Normalize state name to full state name for a given region
 * @param {string} stateText - The state text from geocoder (can be abbreviation or name)
 * @param {string} region - The region code ('US', 'AU', etc.)
 * @param {Array} usStateOptions - Optional US state options array (from states-us package)
 * @returns {string} The normalized full state name
 */
export const normalizeStateName = (stateText, region, usStateOptions = null) => {
  if (!stateText) return '';
  
  const normalizedText = stateText.trim();
  
  if (region === 'US') {
    // For US, use stateOptions from states-us package
    const stateOptions = usStateOptions || getUSStates();
    if (stateOptions && stateOptions.length > 0) {
      const state = stateOptions.find(
        (item) => 
          item.name.toLowerCase() === normalizedText.toLowerCase() || 
          item.abbreviation.toLowerCase() === normalizedText.toLowerCase()
      );
      return state ? state.name : normalizedText;
    }
    return normalizedText;
  } else if (region === 'AU') {
    // Australian states mapping
    const matchedState = AU_STATES.find(state => 
      state.names.some(name => name.toLowerCase() === normalizedText.toLowerCase())
    );
    
    return matchedState ? matchedState.fullName : normalizedText;
  }
  
  // For other regions (NZ, etc.), return as-is
  return normalizedText;
};

/**
 * Vue.js mixin/helper for normalizing state names
 * Can be used in Vue components with region detection
 */
export const stateNormalizerMixin = {
  methods: {
    normalizeStateName(stateText, usStateOptions = null) {
      const region = process.env.VUE_APP_REGION || '';
      return normalizeStateName(stateText, region, usStateOptions || this.stateOptions);
    },
  },
};

export default {
  normalizeStateName,
  getUSStates,
  getAUStates,
  getAUStatesForSelect,
  stateNormalizerMixin,
};

